import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';
import { verificationService } from '../../../services/supabaseService';
import { useAuth } from '../../../contexts/AuthContext';

const VerificationBadgesSection = ({ userProfile, expanded = false, onVerificationComplete }) => {
  const { user } = useAuth();
  const [expandedItem, setExpandedItem] = useState(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [identityModalOpen, setIdentityModalOpen] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Email is always considered verified if user has an account (Supabase email confirmation)
  const emailVerified = user?.email_confirmed_at || userProfile?.emailVerified !== false;

  const verificationTypes = [
    {
      id: 'email',
      name: 'Email Address',
      icon: 'Mail',
      description: 'Confirm your email for account recovery',
      completed: emailVerified,
      steps: ['Confirm your email address', 'Check your inbox for verification link', 'Click the link to verify'],
      action: null
    },
    {
      id: 'photo',
      name: 'Photo Authenticity',
      icon: 'Camera',
      description: 'Verify your photos are genuine and recent',
      completed: userProfile?.photoVerificationStatus === 'approved' || userProfile?.photoVerified === true,
      status: userProfile?.photoVerificationStatus || (userProfile?.photoVerified ? 'approved' : null),
      steps: [
        'Upload a clear selfie showing your face',
        'Ensure good lighting and no filters',
        'Wait for admin review (typically 24–48 hours)'
      ],
      action: 'photo'
    },
    {
      id: 'identity',
      name: 'Identity Verification',
      icon: 'IdCard',
      description: 'Verify your identity with government ID',
      completed: userProfile?.identityVerificationStatus === 'approved' || userProfile?.identityVerified === true,
      status: userProfile?.identityVerificationStatus || (userProfile?.identityVerified ? 'approved' : null),
      steps: [
        'Prepare a valid government-issued ID (passport, driver\'s licence)',
        'Take a clear photo of your ID',
        'Take a selfie for comparison',
        'Submit for review (typically 24–48 hours)'
      ],
      action: 'identity'
    }
  ];

  const completedCount = verificationTypes?.filter((v) => v?.completed)?.length;
  const totalCount = verificationTypes?.length;

  const handlePhotoSubmit = async () => {
    if (!user?.id) {
      setError('Please log in to verify your account.');
      return;
    }
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      console.log('Submitting photo verification for user:', user.id, 'File:', photoFile?.name);
      const result = await verificationService.submitPhotoVerification(user.id, photoFile || undefined);
      console.log('Photo verification successful:', result);
      setSuccess('Photo verification submitted successfully!');
      // Wait a moment to show success message, then close and refresh
      setTimeout(() => {
        setPhotoModalOpen(false);
        setPhotoFile(null);
        setSuccess(null);
        // Refresh profile to show updated badge
        if (onVerificationComplete) {
          onVerificationComplete();
        }
      }, 1500);
    } catch (err) {
      console.error('Photo verification error:', err);
      setError(err?.message || 'Failed to submit verification. Please check the console for details and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleIdentitySubmit = async () => {
    if (!user?.id) {
      setError('Please log in to verify your account.');
      return;
    }
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      console.log('Submitting identity verification for user:', user.id, 'ID file:', idFile?.name, 'Selfie:', selfieFile?.name);
      const result = await verificationService.submitIdentityVerification(user.id, idFile || undefined, selfieFile || undefined);
      console.log('Identity verification successful:', result);
      setSuccess('Identity verification submitted successfully!');
      // Wait a moment to show success message, then close and refresh
      setTimeout(() => {
        setIdentityModalOpen(false);
        setIdFile(null);
        setSelfieFile(null);
        setSuccess(null);
        // Refresh profile to show updated badge
        if (onVerificationComplete) {
          onVerificationComplete();
        }
      }, 1500);
    } catch (err) {
      console.error('Identity verification error:', err);
      setError(err?.message || 'Failed to submit verification. Please check the console for details and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-1">Verification Badges</h2>
          <p className="text-sm text-muted-foreground">
            {completedCount} of {totalCount} verifications completed
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-2xl font-bold text-primary">{totalCount ? Math.round((completedCount / totalCount) * 100) : 0}%</div>
        </div>
      </div>

      <div className="mb-6">
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${totalCount ? (completedCount / totalCount) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="space-y-4">
        {verificationTypes?.map((verification) => (
          <div
            key={verification?.id}
            className={cn(
              'border rounded-lg transition-all',
              verification?.completed ? 'border-success/30 bg-success/5' : 'border-border bg-background'
            )}
          >
            <div className="p-4">
              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    'p-3 rounded-lg',
                    verification?.completed ? 'bg-success/20' : 'bg-muted'
                  )}
                >
                  <Icon
                    name={verification?.icon}
                    size={24}
                    className={verification?.completed ? 'text-success' : 'text-muted-foreground'}
                  />
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-foreground">{verification?.name}</h3>
                    {verification?.status === 'approved' && (
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-success/20 rounded-full">
                        <Icon name="CheckCircle" size={14} className="text-success" />
                        <span className="text-xs font-medium text-success">Verified</span>
                      </div>
                    )}
                    {verification?.status === 'pending' && (
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-warning/20 rounded-full">
                        <Icon name="Clock" size={14} className="text-warning" />
                        <span className="text-xs font-medium text-warning">Pending Review</span>
                      </div>
                    )}
                    {verification?.status === null && verification?.action && (
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-muted/50 rounded-full">
                        <Icon name="AlertCircle" size={14} className="text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground">Not Started</span>
                      </div>
                    )}
                    {verification?.status === 'rejected' && (
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-error/20 rounded-full">
                        <Icon name="XCircle" size={14} className="text-error" />
                        <span className="text-xs font-medium text-error">Rejected</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{verification?.description}</p>

                  {expanded && (
                    <div className="space-y-2">
                      {!verification?.completed && verification?.action !== 'email' && (
                        <>
                          <button
                            onClick={() =>
                              setExpandedItem(expandedItem === verification?.id ? null : verification?.id)
                            }
                            className="text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            <span>{expandedItem === verification?.id ? 'Hide' : 'Show'} verification steps</span>
                            <Icon
                              name="ChevronDown"
                              size={16}
                              className={cn('transition-transform', expandedItem === verification?.id && 'rotate-180')}
                            />
                          </button>

                          {expandedItem === verification?.id && (
                            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                              <p className="text-sm font-medium text-foreground mb-2">Steps to verify:</p>
                              <ol className="space-y-1">
                                {verification?.steps?.map((step, index) => (
                                  <li key={index} className="text-sm text-muted-foreground flex gap-2">
                                    <span className="font-medium">{index + 1}.</span>
                                    <span>{step}</span>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {verification?.status !== 'approved' && verification?.action === 'photo' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      iconName={verification?.status === 'pending' ? 'Clock' : 'ArrowRight'}
                      iconPosition="right"
                      onClick={() => {
                        if (verification?.status === 'pending') {
                          // Show message that it's under review
                          setError('Your photo verification is currently under review. Please wait for admin approval.');
                        } else {
                          setPhotoModalOpen(true);
                        }
                      }}
                    >
                      {verification?.status === 'pending' ? 'Under Review' : verification?.status === 'rejected' ? 'Resubmit' : 'Start Verification'}
                    </Button>
                  )}

                  {verification?.status !== 'approved' && verification?.action === 'identity' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      iconName={verification?.status === 'pending' ? 'Clock' : 'ArrowRight'}
                      iconPosition="right"
                      onClick={() => {
                        if (verification?.status === 'pending') {
                          // Show message that it's under review
                          setError('Your identity verification is currently under review. Please wait for admin approval.');
                        } else {
                          setIdentityModalOpen(true);
                        }
                      }}
                    >
                      {verification?.status === 'pending' ? 'Under Review' : verification?.status === 'rejected' ? 'Resubmit' : 'Start Verification'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!expanded && completedCount < totalCount && (
        <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary mt-0.5" />
            <div>
              <p className="text-sm text-foreground font-medium mb-1">Why verify your account?</p>
              <p className="text-sm text-muted-foreground">
                Verified accounts receive more trust from other members, appear higher in search results, and unlock
                premium features.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Photo Authenticity Modal */}
      {photoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-xl shadow-warm-lg border border-border max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-foreground mb-2">Photo Authenticity Verification</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Upload a clear selfie. It should match your profile photos and show your face clearly.
            </p>
            
            <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-sm font-semibold text-foreground mb-2">📸 Pose Requirements:</p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                <li>Face the camera directly (front-facing)</li>
                <li>Show your full face clearly</li>
                <li>Good lighting - no shadows on face</li>
                <li>No filters or editing</li>
                <li>Match your profile photos</li>
              </ul>
            </div>
            <label className="block mb-4">
              <span className="text-sm font-medium text-foreground">Selfie (optional but recommended)</span>
              <input
                type="file"
                accept="image/*"
                className="mt-2 block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary file:text-primary-foreground"
                onChange={(e) => {
                  const file = e?.target?.files?.[0] || null;
                  setPhotoFile(file);
                  if (file) {
                    console.log('Photo file selected:', file.name, file.size, 'bytes');
                  }
                }}
              />
              {photoFile && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Selected: {photoFile.name} ({(photoFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </label>
            {success && (
              <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg">
                <p className="text-sm text-success font-medium">{success}</p>
              </div>
            )}
            {error && (
              <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg">
                <p className="text-sm text-error font-medium">{error}</p>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setPhotoModalOpen(false); setError(null); setSuccess(null); setPhotoFile(null); }}>
                Cancel
              </Button>
              <Button onClick={handlePhotoSubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit verification'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Identity Verification Modal */}
      {identityModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-xl shadow-warm-lg border border-border max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-foreground mb-2">Identity Verification</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Upload a photo of your ID and a selfie for comparison. Documents are stored securely.
            </p>
            
            <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-sm font-semibold text-foreground mb-2">📸 Selfie Pose Requirements:</p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside mb-3">
                <li>Face the camera directly (front-facing)</li>
                <li>Hold your ID next to your face</li>
                <li>Both your face and ID must be clearly visible</li>
                <li>Good lighting - no shadows</li>
                <li>No filters or editing</li>
              </ul>
              <p className="text-sm font-semibold text-foreground mb-2">🆔 ID Document Requirements:</p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                <li>Government-issued ID (Passport, Driver's License, etc.)</li>
                <li>All text must be clearly readable</li>
                <li>No glare or reflections</li>
                <li>Full document visible in frame</li>
              </ul>
            </div>
            <label className="block mb-3">
              <span className="text-sm font-medium text-foreground">ID document (optional)</span>
              <input
                type="file"
                accept="image/*,.pdf"
                className="mt-2 block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary file:text-primary-foreground"
                onChange={(e) => {
                  const file = e?.target?.files?.[0] || null;
                  setIdFile(file);
                  if (file) console.log('ID file selected:', file.name);
                }}
              />
              {idFile && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Selected: {idFile.name} ({(idFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </label>
            <label className="block mb-4">
              <span className="text-sm font-medium text-foreground">Selfie for comparison (optional)</span>
              <input
                type="file"
                accept="image/*"
                className="mt-2 block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary file:text-primary-foreground"
                onChange={(e) => {
                  const file = e?.target?.files?.[0] || null;
                  setSelfieFile(file);
                  if (file) console.log('Selfie file selected:', file.name);
                }}
              />
              {selfieFile && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Selected: {selfieFile.name} ({(selfieFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </label>
            {success && (
              <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg">
                <p className="text-sm text-success font-medium">{success}</p>
              </div>
            )}
            {error && (
              <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg">
                <p className="text-sm text-error font-medium">{error}</p>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setIdentityModalOpen(false);
                  setError(null);
                  setSuccess(null);
                  setIdFile(null);
                  setSelfieFile(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleIdentitySubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit verification'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationBadgesSection;
